import notifiers from '@/utils/notifiers';
import api from '../_api';

export default {

    /**
     * Makes the API GET request for the costable with costs.
     * @param {String} type Costable type ('event', 'quote', 'package').
     * @param {Number} id Costable ID.
     */
    async loadCostable({ commit, dispatch }, { type, id, pageLoad = true, queryStringObject = {} }) {
        if (pageLoad) {
            commit('setIsCostablePageLoading', true);
        } else {
            commit('setIsCostableTableLoading', true);
        }

        const costable = await api.getCostable(type, id, queryStringObject);
        commit('setCostable', { costable, type, id });

        if ((costable.progress?.max ?? 0) > (costable.progress?.step ?? 0)) {
            dispatch('setProgress', costable.progress);
        } else {
            commit('setIsCostablePageLoading', false);
            commit('setIsCostableTableLoading', false);
        }
    },

    setProgress({ commit, dispatch, state }, progress) {
        // Clear any existing interval
        if (state.progressIntervalId) {
            clearInterval(state.progressIntervalId);
            commit('SET_PROGRESS_INTERVAL', null);
        }

        // Set initial progress
        commit('SET_PROGRESS', progress);

        // Create new interval to check progress every 5 seconds
        const intervalId = setInterval(() => {
            dispatch('getProgress');
        }, 1000);

        commit('SET_PROGRESS_INTERVAL', intervalId);
    },

    async getProgress({ getters, commit, dispatch }) {
        const progress = await api.getProgress(getters.costableType, getters.costableId);

        if (progress) {
            commit('SET_PROGRESS', progress);
        } else {
            commit('SET_PROGRESS', null);
            // Clear interval if progress is null
            if (getters.progressIntervalId) {
                clearInterval(getters.progressIntervalId);
                commit('SET_PROGRESS_INTERVAL', null);
            }

            await dispatch('reloadCostable');
        }
    },

    /**
     * Re-GETs the current costable.
     * Triggers the table loading action instead of the page loading action by using the pageLoad: false option.
     * Includes tabId.
     */
    reloadCostable({ dispatch }, payload = { resetFilters: false }) {
        if (payload.resetFilters) {
            dispatch('resetFilters');
        } else {
            dispatch('applyFilters', { shouldNotify: false });
        }
    },

    /**
     * Sets all costs' selected property to the given boolean value.
     * @param {Boolean} value
     */
    selectAll({ commit }, value) {
        commit('selectAll', value);
    },

    /**
     * Sets a group's selected property to the given boolean value.
     * @param {Number} groupId
     * @param {Boolean} value
     */
    selectGroup({ commit }, { groupId, value }) {
        commit('selectGroup', { groupId, value });
    },

    /**
     * Sets a cost's selected property to the given boolean value.
     * @param {Number} costId
     * @param {Boolean} value
     */
    selectCost({ commit }, { groupId, costId, value }) {
        commit('selectCost', { groupId, costId, value });
    },

    /**
     * Stores a new cost.
     * @param {Object} costData
     */
    async storeCost({ getters, dispatch }, costData) {
        await api.storeCost(getters.costableType, getters.costableId, getters.tabId, true, costData);
        await dispatch('reloadCostable');
        notifiers.success('Cost added.');
    },

    /**
     * Stores a new cost but doesn't build it.
     * @param {Object} costData
     */
    async storeCostWithoutBuildingCost({ getters, dispatch }, costData) {
        await api.storeCost(getters.costableType, getters.costableId, getters.tabId, false, costData);
        await dispatch('reloadCostable', { resetFilters: true });
        notifiers.success('Cost added.');
    },

    /**
     * Updates a cost.
     * @param {Object} costData
     */
    async updateCost({ getters, dispatch }, costData) {
        await api.updateCost(getters.costableType, getters.costableId, costData);
        await dispatch('reloadCostable');
        notifiers.success('Cost edited.');
    },

    /**
     * Partially updates a cost.
     * @param {Array} costIds The ids of the costs we will patch.
     * @param {Object} data The key:value attributes of the cost(s) to be patched.
     */
    async patchCosts({ getters, dispatch }, { costIds, data }) {
        await api.patchCosts(getters.costableType, getters.costableId, costIds, data);
        await dispatch('applyFilters', { shouldNotify: false });
        notifiers.success(costIds.length > 1 ? 'Costs updated.' : 'Cost updated.');
    },

    /**
     * Partially updates a cost by a percentage multiplier.
     * @param {Array} costIds The ids of the costs we will patch.
     * @param {Object} data The key:value attributes of the cost(s) to be patched.
     */
    async patchCostsMultiplier({ getters, dispatch }, { costIds, data }) {
        await api.patchCostsMultiplier(getters.costableType, getters.costableId, costIds, data);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length > 1 ? 'Costs updated.' : 'Cost updated.');
    },

    /**
     * Duplicates a cost.
     * @param {Number} costId
     */
    async duplicateCost({ getters, dispatch }, costId) {
        await api.duplicateCost(getters.costableType, getters.costableId, costId);
        await dispatch('reloadCostable');
        notifiers.success('Cost duplicated.');
    },

    /**
     * Deletes a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async deleteCosts({ getters, dispatch }, costIds) {
        await api.deleteCosts(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost deleted.' : 'Costs deleted.');
    },

    /**
     * Deletes a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async removeCostFromGroup({ getters, dispatch }, costIds) {
        await api.removeCostFromGroup(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost removed from group.' : 'Costs removed from group.');
    },

    /**
     * Deletes a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async removeCostFromTab({ getters, dispatch }, costIds) {
        await api.removeCostFromTab(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost removed from tab.' : 'Costs removed from tab.');
    },

    /**
     * Approve a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async approveCosts({ getters, dispatch }, costIds) {
        await api.approveCosts(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost approved.' : 'Costs approved.');
    },

    /**
     * Approve a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async unapproveCosts({ getters, dispatch }, costIds) {
        await api.unapproveCosts(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost unapproved.' : 'Costs unapproved.');
    },

    /**
     * Reconciles a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async reconcileCosts({ getters, dispatch }, costIds) {
        await api.reconcileCosts(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost reconciled.' : 'Costs reconciled.');
    },

    /**
     * Unreconciles a single or multiple costs by ID.
     * @param {Array|Number} costIds
     */
    async unreconcileCosts({ getters, dispatch }, costIds) {
        await api.unreconcileCosts(getters.costableType, getters.costableId, costIds);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost unreconciled.' : 'Costs unreconciled.');
    },

    /**
     * Save cost as quote.
     * @param {Array|Number} costIds
     */
    saveCostsAsQuote({ getters }, costIds) {
        return api.saveCostAsQuote(getters.costableType, getters.costableId, costIds);
        // notifiers.success('Selected Costs saved as quote');
    },

    /**
     * Gets resource allocations for a cost.
     * @param {Number} costId
     */
    getCostResourceAllocations({ getters }, { costId, filters: { typeId, supplierId, parentResource, searchQuery,
        onlyAllocated, showArchived, useDefaultTypeId, sort, direction }, page }) {
        return api.getCostResourceAllocations(
            getters.costableType,
            getters.costableId,
            costId,
            typeId,
            supplierId,
            parentResource,
            searchQuery,
            onlyAllocated,
            showArchived,
            page,
            useDefaultTypeId,
            sort,
            direction,
        );
    },

    /**
     * Partially updates a resource allocation for a cost.
     * @param {Array} costId
     * @param {Array} resourceId
     * @param {Object} data
     */
    async patchCostResourceAllocation({ getters }, { costId, resourceId, data }) {
        await api.patchCostResourceAllocation(getters.costableType, getters.costableId, costId, resourceId, data);
        notifiers.success('Resource updated.');
    },

    /**
     * Creates a group.
     * @param {String} name
     */
    async storeGroup({ getters, dispatch }, { name, description = null }) {
        const { id } = await api.storeGroup(getters.costableType, getters.costableId, getters.tabId, name, description);
        await dispatch('reloadCostable');
        notifiers.success('Group added.');
        return id;
    },

    /**
     * Adds a single or multiple costs to a group.
     * @param {Array} costIds
     * @param {Number} groupId
     */
    async addCostsToGroup({ getters, dispatch }, { costIds, groupId }) {
        await api.addCostsToGroup(getters.costableType, getters.costableId, costIds, groupId);
        await dispatch('reloadCostable');
        notifiers.success(costIds.length > 1 ? `${costIds.length} costs added to group.` : 'Cost added to group.');
    },

    /**
     * Duplicates a group by ID.
     * @param {Number} groupId
     */
    async duplicateGroup({ getters, dispatch }, groupId) {
        await api.duplicateGroup(getters.costableType, getters.costableId, groupId);
        await dispatch('reloadCostable');
        notifiers.success('Group duplicated.');
    },

    /**
     * Deletes a group by ID.
     * @param {Number} groupId
     */
    async deleteGroup({ getters, dispatch }, groupId) {
        await api.deleteGroup(getters.costableType, getters.costableId, groupId);
        await dispatch('reloadCostable');
        notifiers.success('Group deleted.');
    },

    /**
     * Renames a group by ID.
     * @param {Number} groupId
     * @param {String} name
     * @param {String} description
     */
    async renameGroup({ getters, dispatch }, { groupId, name, description }) {
        await api.updateGroup(getters.costableType, getters.costableId, groupId, {
            name,
            description,
        });
        await dispatch('reloadCostable');
        notifiers.success('Group renamed.');
    },

    /**
     * Creates a new tab.
     * @param {String} name
     * @param {String} description
     */
    async storeTab({ getters, dispatch }, { name, description = null, footer = null, hiddenColumns = null, hideCosts = false }) {
        const tab = await api.storeTab(getters.costableType, getters.costableId, name, description, footer, hiddenColumns, hideCosts);
        await dispatch('changeTab', tab.id);
        notifiers.success('Tab added.');
        return tab.id;
    },

    /**
     * Updates a tab.
     * @param {String} name
     * @param {String} description
     */
    async updateTab({ getters, dispatch }, { tabId, name, description, footer, hiddenColumns, hideCosts }) {
        await api.updateTab(getters.costableType, getters.costableId, tabId, name, description, footer, hiddenColumns, hideCosts);
        // could prolly just return the tab and update the UI here as it's just a name/description change,
        // but let's remain consistent and call reloadCostable. What's *one more* GET request, anyway, right?
        await dispatch('reloadCostable');
        notifiers.success('Tab updated.');
    },

    /**
     * Duplicates a tab.
     * @param {String} name
     * @param {String} description
     */
    async duplicateTab({ getters, dispatch }, { tabId, name, description }) {
        await api.duplicateTab(getters.costableType, getters.costableId, tabId, name, description);
        await dispatch('reloadCostable');
        notifiers.success('Tab duplicated.');
    },

    /**
     * Deletes a tab.
     * @param {String} name
     * @param {String} description
     */
    async deleteTab({ getters, dispatch }, { tabId, deleteGroupsAndCosts }) {
        await api.deleteTab(getters.costableType, getters.costableId, tabId, deleteGroupsAndCosts);
        await dispatch('changeTab', null);
        await dispatch('reloadCostable');
        notifiers.success('Tab deleted.');
    },

    /**
     * Changes the current tab.
     * @param {Number} tabId
     */
    async changeTab({ dispatch, commit }, tabId) {
        commit('setTabId', tabId);
        await dispatch('reloadCostable');
    },

    /**
     * Adds a group to a tab.
     * @param {Number} groupId
     * @param {Number} tabId
     */
    async addGroupToTab({ getters, dispatch }, { groupId, tabId }) {
        await api.addGroupToTab(getters.costableType, getters.costableId, groupId, tabId);
        await dispatch('reloadCostable');
        notifiers.success('Group added to tab.');
    },

    /**
     * Adds a single or multiple costs to a tab.
     * @param {Number} groupId
     * @param {Number} tabId
     */
    async addCostsToTab({ getters, dispatch }, { costIds, tabId }) {
        await api.patchCosts(getters.costableType, getters.costableId, costIds, {
            tab_id: tabId,
        });
        await dispatch('reloadCostable');
        notifiers.success(costIds.length === 1 ? 'Cost added to tab.' : 'Costs added to tab.');
    },

    /**
     * Adds a package.
     * @param {Number} groupId
     * @param {String} name
     */
    async addPackage({ getters, dispatch }, { selectedPackageId, setCurrency }) {
        await api.addPackage(getters.costableType, getters.costableId, getters.tabId, selectedPackageId, setCurrency);
        await dispatch('reloadCostable');
        notifiers.success('Package added.');
    },

    /**
     * Updates the costable contingency.
     */
    async updateContingency({ dispatch, getters },
        { internalCostPercentage, internalCostMinimum, externalPricePercentage, externalPriceMinimum }) {
        await api.updateContingency(
            getters.costableType, getters.costableId,
            internalCostPercentage, internalCostMinimum, externalPricePercentage, externalPriceMinimum
        );
        await dispatch('reloadCostable');
        notifiers.success('Contingency updated.');
    },

    async updateServiceCharge({ dispatch, getters }, serviceCharge) {
        await api.patchCostable(
            getters.costableType,
            getters.costableId,
            { service_charge: serviceCharge },
        );
        await dispatch('reloadCostable');
        notifiers.success('Service charge updated.');
    },

    /**
     * Applies the filters.
     * @param {Boolean} shouldNotify If set to true, a "filters applied" notification will be shown.
     */
    async applyFilters({ dispatch, state }, { shouldNotify }) {
        const queryStringObject = {};
        if (state.filters.type !== null) {
            queryStringObject.cost_type_id = state.filters.type.id;
        }
        if (state.filters.status !== null) {
            queryStringObject.cost_status_id = state.filters.status.id;
        }
        if (state.filters.reconciled !== null) {
            queryStringObject.is_reconciled = state.filters.reconciled.id;
        }
        if (state.filters.search !== '') {
            queryStringObject.filter = state.filters.search;
        }
        if (state.filters.vatable !== null) {
            queryStringObject.is_vatable = state.filters.vatable.id;
        }
        if (state.filters.billable !== null) {
            queryStringObject.is_billable = state.filters.billable.id;
        }
        if (state.tabId) {
            queryStringObject.tab = state.tabId;
        }
        if (state.filters.user !== null) {
            queryStringObject.user_id = state.filters.user.id;
        }
        if (state.filters.transaction_type !== null) {
            queryStringObject.transaction_type_id = state.filters.transaction_type.id;
        }
        if (state.filters.secondary_status !== null) {
            queryStringObject.secondary_status_id = state.filters.secondary_status.id;
        }

        await dispatch('loadCostable', { type: state.costableType, id: state.costableId, pageLoad: false, queryStringObject });

        if (shouldNotify) {
            notifiers.success('Filters applied.');
        }
    },

    /**
     * Resets filters to their default values.
     */
    async resetFilters({ dispatch }) {
        await dispatch('updateFilters', {
            type: null,
            status: null,
            reconciled: null,
            search: '',
            vatable: null,
            billable: null,
            user: null,
            transaction_type: null,
            secondary_status: null,
        });
        await dispatch('applyFilters', { shouldNotify: false });
        notifiers.success('Filters reset.');
    },

    /**
     * Sets filter values.
     * @param {Object} value
     */
    updateFilters({ commit }, value) {
        commit('setFilters', value);
    },

    /**
     * Exclusively refreshes the reported information data.
     */
    async refreshReportedInformation({ dispatch, getters }) {
        await api.refreshReportedInformation(getters.costableType, getters.costableId);
        await dispatch('reloadCostable');
        notifiers.success('Reported information refreshed.');
    },

    /**
     * Adds a currency to the costable.
     * @param {Number} currencyId
     * @param {String} exchangeRate
     */
    async storeCurrency({ dispatch, getters }, { currencyId, exchangeRate }) {
        await api.storeCurrency(getters.costableType, getters.costableId, currencyId, exchangeRate);
        await dispatch('reloadCostable');
        notifiers.success('Currency added.');
    },

    /**
     * Updates a currency.
     * @param {Number} currencyId
     * @param {String} exchangeRate
     */
    async updateCurrency({ dispatch, getters }, { currencyId, exchangeRate }) {
        await api.updateCurrency(getters.costableType, getters.costableId, currencyId, exchangeRate);
        await dispatch('reloadCostable');
        notifiers.success('Currency updated.');
    },

    /**
     * Deletes a currency.
     * @param {Number} currencyId
     */
    async deleteCurrency({ dispatch, getters }, currencyId) {
        await api.deleteCurrency(getters.costableType, getters.costableId, currencyId);
        await dispatch('reloadCostable');
        notifiers.success('Currency deleted.');
    },

    /**
     * Updates event pricing mode.
     * @param {Number} pricingMode
     * @param {String} feeAmount
     * @param {String} feeTaxRateId
     */
    async updateEventPricingMode({ dispatch, getters }, { pricingMode, feeAmount, feeTaxRateId }) {
        await api.updateEventPricingMode(getters.costableType, getters.costableId, pricingMode, feeAmount, feeTaxRateId);
        await dispatch('reloadCostable');
        notifiers.success('Event pricing mode updated.');
    },

    /**
     * Updates quote pricing mode.
     * @param {Number} pricingMode
     * @param {String} feeAmount
     */
    async updateQuotePricingMode({ dispatch, getters }, { acceptedQuoteId, pricingMode, feeAmount, feeTaxRateId }) {
        await api.updateQuotePricingMode(getters.costableType, getters.costableId, acceptedQuoteId, pricingMode, feeAmount, feeTaxRateId);
        await dispatch('reloadCostable');
        notifiers.success('Quote pricing mode updated.');
    },

    /**
     * Updates the order of cost groups.
     * @param {Array} groups
     */
    async updateGroupsOrder({ commit, dispatch, getters }, groups) {
        commit('setGroups', groups); // we do this so that the groups don't jump around
        const groupIds = groups.map(group => group.id).filter(id => id); // filter to remove other costs null value
        await api.updateGroupsOrder(getters.costableType, getters.costableId, groupIds);
        await dispatch('reloadCostable'); // not necessary here as we commit the group order
        notifiers.success('Group moved.');
    },

    /**
     * Updates group costs (including order) when moving a cost between a group or within a group.
     * This action fires once for an internal move and twice for a move between groups
     * (once for the cost leaving and once for the cost entering).
     * @param {Number} groupId
     * @param {Array} costs
     */
    async setCostsAfterMoving({ commit, dispatch, getters }, { groupId, costs, isLeavingGroup }) {
        commit('setGroupCosts', { groupId, costs }); // we do this so that the costs don't jump around
        const costIds = costs.map(cost => cost.id);
        await api.updateGroupCostsOrder(getters.costableType, getters.costableId, costIds, groupId);
        if (!isLeavingGroup) {
            await dispatch('reloadCostable'); // not necessary here as we commit the group order
            notifiers.success('Cost moved.');
        }
    },

    getEditableUid({ state, commit }) {
        const uid = state.nextEditableUid;
        commit('incrementEditableUid');
        return uid;
    },

    /**
     * Updates the order of cost tabs.
     * @param {Array} tabs
     */
    async updateTabsOrder({ commit, dispatch, getters }, tabs) {
        commit('setTabs', tabs); // we do this so that the tabs don't jump around
        const tabIds = tabs.map(tab => tab.id).filter(id => id); // filter to remove other costs null value
        await api.updateTabsOrder(getters.costableType, getters.costableId, tabIds);
        await dispatch('reloadCostable'); // not necessary here as we commit the group order
        notifiers.success('Tab moved.');
    },

    async loadSettings({ commit }) {
        const response = await api.getSettings();

        commit('SET_SETTINGS', response);
        commit('SET_HIDDEN_COLUMNS', response.hidden_columns);
    },

    setServiceCharge({ commit }, serviceCharge) {
        commit('SET_SERVICE_CHARGE', serviceCharge);
    },

    setHiddenColumns({ commit }, columns) {
        commit('SET_HIDDEN_COLUMNS', columns);
    },

    async saveColumnSettings({ getters }) {
        await api.saveColumnSettingsttings(getters.hiddenColumns);
    },

};
